import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';

import { AppDatePickerComponent } from '../shared/ui/app-date-picker/app-date-picker.component';
import { AppLookupComponent } from '../shared/ui/app-lookup/app-lookup.component';
import { AppMobileInputComponent } from '../shared/ui/app-mobile-input/app-mobile-input.component';
import { TempleRegistrationPayload, TempleRegistrationService } from './temple-registration.service';

/** Language options — the same four the login screen offers. */
export interface RegistrationLanguage {
  id: string;
  label: string;
}

/** Currency option rendered as "<flag> <country> — <code> (<symbol>)"; the SYMBOL is stored. */
export interface RegistrationCurrency {
  /** The stored temple_configuration CURRENCY value (a symbol, as it has always been). */
  symbol: string;
  flag: string;
  country: string;
  code: string;
}

/** Date-format option; `sample` is rendered from TODAY so the list is self-explanatory. */
export interface RegistrationDateFormat {
  pattern: string;
  sample: string;
}

/**
 * WS-DBM Phase 3 — the first-run registration screen.
 *
 * A freshly installed counter has an empty database: no temple, no user, and the
 * login screen cannot resolve a tenant. `login-stoda` routes here when the
 * `all` feed comes back with no temple; this form registers the temple with the
 * cloud (which assigns the code) and provisions the local installation, then
 * hands over to the login screen.
 *
 * Internet is required (owner decision) — the code is always cloud-assigned, so
 * an unreachable server is a hard refusal rather than a provisional local code.
 *
 * R69 (owner directive 2026-07-31) reshaped it to the pooja-v2 look: ONE
 * viewport-height page laid out four fields to a row so nothing scrolls, and the
 * administrator no longer types a password — the server generates a 6-digit
 * temporary one and WhatsApps it to the number entered here.
 */
@Component({
  selector: 'app-temple-registration',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AppDatePickerComponent, AppLookupComponent, AppMobileInputComponent],
  templateUrl: './temple-registration.component.html',
  styleUrls: ['./temple-registration.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TempleRegistrationComponent implements OnInit {

  private fb = inject(FormBuilder);
  private service = inject(TempleRegistrationService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  form: FormGroup;
  /** Blocks submission while a request is in flight. */
  saving = false;
  /** Set from GET bootstrap/status — a warning banner, never a hard block. */
  cloudConfigured = true;
  checking = true;
  errorMessage: string | null = null;
  successCode: string | null = null;
  /** Shown on the success card so the operator knows which account to sign in as. */
  successUserName: string | null = null;
  /**
   * Set the moment the operator types in the user-name box: from then on the
   * name is theirs and the Full-name derivation stops overwriting it.
   */
  userNameEdited = false;

  readonly languages: RegistrationLanguage[] = [
    { id: 'english', label: 'English' },
    { id: 'malayalam', label: 'മലയാളം' },
    { id: 'tamil', label: 'தமிழ்' },
    { id: 'kannada', label: 'ಕನ್ನಡ' }
  ];

  readonly currencies: RegistrationCurrency[] = [
    { symbol: '₹', flag: '🇮🇳', country: 'India', code: 'INR' },
    { symbol: '$', flag: '🇺🇸', country: 'United States', code: 'USD' },
    { symbol: 'A$', flag: '🇦🇺', country: 'Australia', code: 'AUD' },
    { symbol: 'रू', flag: '🇳🇵', country: 'Nepal', code: 'NPR' },
    { symbol: 'RM', flag: '🇲🇾', country: 'Malaysia', code: 'MYR' }
  ];

  /**
   * Built ONCE in the field initializer — a getter minting a fresh array on every
   * change-detection pass is the documented NG0103 trap for `*ngFor` lists that
   * feed an `[ngModel]`/`formControlName` select.
   */
  readonly dateFormats: RegistrationDateFormat[] = TempleRegistrationComponent.buildDateFormats();

  ngOnInit(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      nameShort: ['', Validators.maxLength(100)],
      mainDeity: ['', Validators.required],
      address1: [''],
      city: [''],
      district: [''],
      state: ['Kerala'],
      pincode: [''],
      // The temporary password is delivered here, so it is required.
      mobile: ['', Validators.required],
      email: [''],
      adminUserName: ['', [Validators.required, Validators.maxLength(50)]],
      adminFirstName: [''],
      language: ['english'],
      financialYearStart: [this.defaultFinancialYearStart(), Validators.required],
      currency: ['₹'],
      dateFormat: [this.dateFormats[0].pattern],
      poojaRowLength: [5],
      stockAndInventory: [true],
      showInReports: [true]
    });

    this.service.getStatus().pipe(finalize(() => {
      this.checking = false;
      this.cdr.markForCheck();
    })).subscribe({
      next: (response) => {
        const status = response?.result ?? response;
        if (status?.registered) {
          // Already registered — nothing to do here.
          this.router.navigate(['/login-stoda']);
          return;
        }
        this.cloudConfigured = status?.cloudConfigured !== false;
        this.cdr.markForCheck();
      },
      error: () => {
        // The status probe is advisory; the form stays usable and the real
        // failure (if any) surfaces on submit with the server's message.
        this.cdr.markForCheck();
      }
    });
  }

  /** The four formats the owner listed, sampled with today's date. */
  private static buildDateFormats(): RegistrationDateFormat[] {
    const today = new Date();
    return ['dd-MM-yyyy', 'dd/MM/yyyy', 'MM/dd/yyyy', 'dd-MMM-yyyy'].map(pattern => ({
      pattern,
      sample: formatDate(today, pattern, 'en-US')
    }));
  }

  /** 1 April of the current financial year — the Indian convention. */
  private defaultFinancialYearStart(): string {
    const now = new Date();
    const year = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    return `${year}-04-01`;
  }

  /** Malayalam turns the deity field into a manglish (type-in-English) input. */
  get isMalayalam(): boolean {
    return this.form?.value.language === 'malayalam';
  }

  /**
   * Starting mode for the deity field's Manglish layer. Without it the layer
   * falls back to `CURRENT_USER.typingLanguage`, which a pre-login screen has
   * no answer for — so the മ badge would stay hidden until the operator found
   * F6, exactly the "manglish icon not showing" the owner reported.
   */
  get manglishDefault(): 'manglish' | 'english' {
    return this.isMalayalam ? 'manglish' : 'english';
  }

  /** Returns true if error message indicates temple is already registered in DB. */
  get isAlreadyRegistered(): boolean {
    const msg = (this.errorMessage || '').toLowerCase();
    return msg.includes('already registered') || msg.includes('already has a temple');
  }

  /** Live example beside the Date format label, for the CURRENT selection. */
  get dateFormatSample(): string {
    const selected = this.dateFormats.find(f => f.pattern === this.form?.value.dateFormat);
    return selected ? selected.sample : '';
  }

  /**
   * Title-cases a place/name field AS IT IS TYPED. Only the first letter of
   * each word is touched — the rest is left exactly as entered, so an
   * intentional acronym ("SNDP") survives and Malayalam text (which has no
   * case) is a no-op.
   */
  onTitleCaseInput(event: Event, control: string): void {
    this.reshape(event, control, value => value.replace(/(^|\s)(\S)/g, (_m, gap, first) => gap + first.toUpperCase()));
  }

  /** PIN code: digits only. */
  onDigitsInput(event: Event, control: string): void {
    this.reshape(event, control, value => value.replace(/\D+/g, ''));
  }

  /**
   * The administrator's full name drives the user name until the operator
   * edits that box themselves.
   */
  onFullNameInput(event: Event): void {
    this.reshape(event, 'adminFirstName', value => value);
    if (!this.userNameEdited) {
      // setValue WITH the view update — this writes to the OTHER input.
      this.form.controls['adminUserName'].setValue(this.toUserName(this.form.value.adminFirstName));
    }
  }

  /** A hand-typed user name keeps the same shape, and stops the derivation. */
  onUserNameInput(event: Event): void {
    this.userNameEdited = true;
    this.reshape(event, 'adminUserName', value => this.toUserName(value));
  }

  /** lower-case, spaces to hyphens, every other symbol dropped. */
  private toUserName(value: string): string {
    return (value || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]+/g, '')
      .trim()
      .replace(/[\s-]+/g, '-');
  }

  /**
   * Applies a text transform to what was just typed. The element has to be
   * corrected directly: with a reactive form the control's own value is the
   * only thing `setValue` would refresh, and re-rendering the view would drop
   * the caret to the end of the line on every keystroke — so the DOM is fixed
   * in place (caret preserved) and the model updated without a view write.
   */
  private reshape(event: Event, control: string, transform: (value: string) => string): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }
    const raw = input.value;
    const next = transform(raw);
    if (next !== raw) {
      const caret = input.selectionStart ?? next.length;
      input.value = next;
      const moved = Math.max(0, caret - (raw.length - next.length));
      try {
        input.setSelectionRange(moved, moved);
      } catch {
        // some input types do not support selection ranges
      }
    }
    this.form.controls[control].setValue(next, { emitModelToViewChange: false });
  }

  /** Header picker (no label) — keeps the form control as the source of truth. */
  onLanguagePick(language: string): void {
    this.form.controls['language'].setValue(language);
    this.onLanguageChange();
  }

  onLanguageChange(): void {
    // Best effort: the app reads this after login. Each machine's live choice
    // still belongs to the login screen's own picker, which this never replaces.
    try {
      sessionStorage.setItem('language', this.form.value.language);
    } catch {
      // private-mode / storage-disabled browsers must not break registration
    }
    this.cdr.markForCheck();
  }

  submit(): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.errorMessage = null;

    if (this.form.invalid) {
      this.saving = false;
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const v = this.form.value;
    const payload: TempleRegistrationPayload = {
      name: v.name,
      nameShort: v.nameShort,
      mainDeity: v.mainDeity,
      address1: v.address1,
      city: v.city,
      district: v.district,
      state: v.state,
      pincode: v.pincode,
      mobile: v.mobile,
      email: v.email,
      admin: {
        userName: v.adminUserName,
        firstName: v.adminFirstName,
        phoneNo: v.mobile,
        email: v.email
      },
      config: {
        financialYearStart: v.financialYearStart,
        dateFormat: v.dateFormat,
        currency: v.currency,
        language: v.language,
        poojaRowLength: v.poojaRowLength,
        stockAndInventory: v.stockAndInventory,
        showInReports: v.showInReports
      }
    };

    this.cdr.markForCheck();
    this.service.register(payload).pipe(finalize(() => {
      this.saving = false;
      this.cdr.markForCheck();
    })).subscribe({
      next: (response) => {
        const status = String(response?.status ?? '').toUpperCase();
        if (status && status !== 'SUCCESS') {
          this.errorMessage = response?.message || 'Registration failed.';
          this.cdr.markForCheck();
          return;
        }
        this.successCode = response?.result?.code ?? null;
        this.successUserName = response?.result?.userName ?? this.form.value.adminUserName;
        // The tenant must be re-resolved from the freshly created temple.
        localStorage.removeItem('tuid');
        localStorage.removeItem('temple');
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.errorMessage = err?.error?.message || err?.message || 'Registration failed.';
        this.cdr.markForCheck();
      }
    });
  }

  goToLogin(): void {
    this.router.navigate(['/login-stoda']);
  }
}
